import React, { useMemo } from 'react';
import type { Choice } from '@xoxokiosk/shared/types/story-tree-types';

type Letter = 'E'|'I'|'N'|'S'|'T'|'F'|'P'|'J';

interface MBTISpectrumHUDProps {
  choices?: Choice[];
  // Optional: when provided, HUD will reflect these actual MBTI letter scores
  scores?: Record<Letter, number>;
}

// Heuristic mapping from story traits -> MBTI axes contributions
// Tunable and non-destructive: purely UI feedback for users
const TRAIT_TO_AXIS: Record<string, { axis: 'EI'|'NS'|'TF'|'PJ'; toward: 'E'|'I'|'N'|'S'|'T'|'F'|'P'|'J' }> = {
  adventurous: { axis: 'EI', toward: 'E' },
  social: { axis: 'EI', toward: 'E' },
  bold: { axis: 'EI', toward: 'E' },
  traditional: { axis: 'EI', toward: 'I' },
  analytical: { axis: 'TF', toward: 'T' },
  meticulous: { axis: 'PJ', toward: 'J' },
  spontaneous: { axis: 'PJ', toward: 'P' },
  creative: { axis: 'NS', toward: 'N' },
};

export const MBTISpectrumHUD: React.FC<MBTISpectrumHUDProps> = ({ choices = [], scores: letterScores }) => {
  const scores = useMemo(() => {
    // If actual MBTI scores are provided, normalize per axis and use them
    if (letterScores) {
      const s: Record<Letter, number> = { E:0, I:0, N:0, S:0, T:0, F:0, P:0, J:0 };
      (Object.keys(letterScores) as Letter[]).forEach(k => {
        s[k] = letterScores[k] ?? 0;
      });
      const normPair = (a: 'E'|'N'|'T'|'P', b: 'I'|'S'|'F'|'J') => {
        const sum = (s[a] || 0) + (s[b] || 0);
        if (sum <= 0) return;
        s[a] = (s[a] || 0) / sum;
        s[b] = (s[b] || 0) / sum;
      };
      normPair('E','I');
      normPair('N','S');
      normPair('T','F');
      normPair('P','J');
      return s;
    }

    // Fallback: derive a soft signal from StoryTree choices
    const s: Record<Letter, number> = { E:0,I:0,N:0,S:0,T:0,F:0,P:0,J:0 };
    for (const c of choices) {
      const map = TRAIT_TO_AXIS[c.personalityTrait];
      if (!map) continue;
      const w = Math.max(0, Math.min(1, c.traitStrength || 0));
      s[map.toward] += w;
    }
    const normPair = (a: 'E'|'N'|'T'|'P', b: 'I'|'S'|'F'|'J') => {
      const sum = s[a] + s[b];
      if (sum <= 0) return;
      s[a] = s[a] / sum;
      s[b] = s[b] / sum;
    };
    normPair('E','I');
    normPair('N','S');
    normPair('T','F');
    normPair('P','J');
    return s;
  }, [choices, letterScores]);

  const bars: Array<{ key: 'EI'|'NS'|'TF'|'PJ'; left: string; right: string; leftVal: number; rightVal: number }> = [
    { key: 'EI', left: 'E', right: 'I', leftVal: scores.E, rightVal: scores.I },
    { key: 'NS', left: 'N', right: 'S', leftVal: scores.N, rightVal: scores.S },
    { key: 'TF', left: 'T', right: 'F', leftVal: scores.T, rightVal: scores.F },
    { key: 'PJ', left: 'P', right: 'J', leftVal: scores.P, rightVal: scores.J },
  ];

  return (
    <div className="mbti-hud v">
      {bars.map(b => (
        <div key={b.key} className="mbti-vbar">
          <div className="mbti-vlabel top">{b.left}</div>
          <div className="mbti-vtrack shimmer">
            <div className="mbti-vfill top" style={{ height: `${Math.round((b.leftVal || 0) * 100)}%` }} />
            <div className="mbti-vfill bottom" style={{ height: `${Math.round((b.rightVal || 0) * 100)}%` }} />
          </div>
          <div className="mbti-vlabel bottom">{b.right}</div>
        </div>
      ))}
    </div>
  );
};
